import translation from 'locales/translation';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AlertModal, {
  IAlertModalGenericProps,
} from 'components/Modal/__LEGACY__/AlertModal/AlertModal';

import { FlagColaborador } from 'helpers/enums/TipoColaborador';

import { IAssembleiaContaAssociado } from 'store/modules/AssembleiasAssociadoReducer';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import { IReducer } from 'store/Reducer';

import {
  contaDisponivelParaSelecao,
  contaSelecionadaPeloUsuario,
  getTotalContasDisponiveisParaSelecao,
  getTotalContasSelecionadasPeloUsuario,
} from 'helpers/utils/AssembleiaUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';

import { TipoConta } from 'components/Icons';
import Loading from 'components/Modal/__LEGACY__/AbrirVotacaoPautaModal/Loading';
import ErrorHandler from 'helpers/utils/ErrorHandler';
import {
  ButtonThemes,
  Checkbox,
  CheckboxThemes,
  CheckCircleIcon,
  Col,
  ControlLabel,
  Modal,
  ModalSizes,
  RadioButton,
  RadioButtonType,
  Row,
  ScreenSize,
  useScreenSize,
} from 'plataforma-fundacao-componentes';
import { ModalProps } from 'plataforma-fundacao-componentes/dist/components/modal/Modal';
import {
  assembleiasAssociadoActionsLoadPautas,
  assembleiasAssociadoActionsRegistrarPresenca,
} from 'store/modules/AssembleiasAssociadoActions';
import './styles.scss';
import moment from 'moment';

const rootClassName = 'component-seletor-contas-voto-modal';

interface IModalProps extends ModalProps {
  onConfirm: () => Promise<void>;
}

export default function SeletorContasParaVotoModal(props: IModalProps) {
  const dispatchAction = useDispatch();
  const { currentEvent } = useSelector((state: IReducer) => state.assembleias);
  const { usuario } = useSelector((state: IReducer) => state.auth);
  const [idConvocados, setIdConvocados] = useState<number[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [aceiteCheck, setAceiteCheck] = useState(true);
  const [disabledCheckAceite, setDisabledCheckAceite] = useState(false);
  const [confirmated, setConfirmated] = useState<boolean>(false);

  const handleClose = useCallback(() => {
    props.onClose?.(props.modalKey);
  }, [props]);

  const openAlertModal = useCallback(
    (title: string, text: string) => {
      const modalKey: string = getUniqueKey();
      dispatchAction(
        modalsActionsOpen(
          AlertModal,
          {
            genericProps: {
              title: title,
              contentText: text,
              key: modalKey,
            } as IAlertModalGenericProps,
          },
          modalKey
        )
      );
    },
    [dispatchAction]
  );

  const toCloseAndShowAlert = useCallback(() => {
    if (!currentEvent?.contasDisponiveis || !usuario) return;
    const disponiveis = getTotalContasDisponiveisParaSelecao(
      currentEvent.contasDisponiveis,
      usuario.cpfCnpj
    );
    const selecionadas: number = getTotalContasSelecionadasPeloUsuario(
      currentEvent.contasDisponiveis,
      usuario.cpfCnpj
    );
    if (disponiveis > 0 && selecionadas === 0) {
      // não selecionou nada = aparece
      // abre modal informando que não selecionou nada
      openAlertModal(
        translation.SeletorContasParaVotoModal.title,
        translation.AlertModalRepresentante.naoSelecionouNada.text
      );
      handleClose();
      return;
    }
    if (disponiveis > 0 && selecionadas > 0) {
      // selecionou mas ainda pode selecionar mais = aparece
      // abre modal informando que ainda pode selecionar mais contas
      openAlertModal(
        translation.SeletorContasParaVotoModal.title,
        translation.AlertModalRepresentante.aindaPodeSelecionarMais.text
      );
      handleClose();
      return;
    }
    if (disponiveis === 0) {
      // não tem mais nada para selecionar = não aparece
      handleClose();
      return;
    }
  }, [openAlertModal, handleClose, currentEvent, usuario]);

  const contaSelecionada = useCallback(
    (conta: IAssembleiaContaAssociado) => {
      if (usuario && aceiteCheck) {
        return (
          idConvocados.includes(conta.idConvocado) ||
          contaSelecionadaPeloUsuario(conta, usuario.cpfCnpj)
        );
      }
      return false;
    },
    [usuario, idConvocados, aceiteCheck]
  );

  const handleSelecionarConta = useCallback(
    (conta: IAssembleiaContaAssociado) => {
      let idConvocadosCopy = [...idConvocados];
      const idConvocadoFoundIndex = idConvocadosCopy.indexOf(conta.idConvocado);
      if (idConvocadoFoundIndex > -1) {
        idConvocadosCopy.splice(idConvocadoFoundIndex, 1);
      } else {
        idConvocadosCopy.push(conta.idConvocado);
      }
      setIdConvocados(idConvocadosCopy);
    },
    [idConvocados]
  );

  const canShowAceite = useMemo(() => {
    return (currentEvent?.contasDisponiveis || []).length > 1;
  }, [currentEvent]);

  const salvarPresencas = useCallback(() => {
    if (!currentEvent || !usuario) return;
    const aceite = canShowAceite ? aceiteCheck : null;
    const aceiteReq = aceite !== null ? (aceite ? 1 : 0) : null;
    setSalvando(true);

    dispatchAction(
      assembleiasAssociadoActionsRegistrarPresenca(
        usuario.cpfCnpj,
        aceiteReq,
        idConvocados,
        currentEvent.id,
        () => {
          setConfirmated(true);
          setSalvando(false);
          dispatchAction(
            assembleiasAssociadoActionsLoadPautas(currentEvent.id)
          );
        },
        (err) => {
          const dataFim = moment(currentEvent.dataFim);

          ErrorHandler(err.response!.data, {
            '#dataFim#1#': dataFim.format('DD/MM/YYYY'),
            '#dataFim#2#': dataFim.format('HH:mm'),
          });
          setSalvando(false);
        }
      )
    );
  }, [
    canShowAceite,
    currentEvent,
    usuario,
    aceiteCheck,
    idConvocados,
    dispatchAction,
  ]);

  const contaElegivelParaSelecao = useCallback(
    (conta: IAssembleiaContaAssociado): boolean => {
      if (!usuario) return false;
      return contaDisponivelParaSelecao(conta, usuario.cpfCnpj);
    },
    [usuario]
  );

  const contaDisabled = (conta: IAssembleiaContaAssociado): boolean => {
    return !contaElegivelParaSelecao(conta);
  };

  const isColaborador = useMemo(() => {
    if (!currentEvent?.contasDisponiveis) return false;
    return currentEvent.contasDisponiveis.some(
      (conta) => conta.flgColaborador === FlagColaborador.Colaborador
    );
  }, [currentEvent]);

  const isExColaborador = useMemo(() => {
    if (!currentEvent?.contasDisponiveis) return false;
    return currentEvent.contasDisponiveis.some(
      (conta) => conta.flgExColaborador === true
    );
  }, [currentEvent]);

  useEffect(() => {
    if (confirmated) {
      toCloseAndShowAlert();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmated, currentEvent?.contasDisponiveis]);

  useEffect(() => {
    if (initialized || !currentEvent?.contasDisponiveis || !usuario) return;
    setInitialized(true);
    let idConvocadosCopy: number[] = [
      ...idConvocados,
      ...currentEvent.contasDisponiveis
        .filter((conta) => {
          // já está selecionado na modal
          if (idConvocados.includes(conta.idConvocado)) {
            return false;
          }
          if (
            !contaDisponivelParaSelecao(conta, usuario.cpfCnpj) &&
            !contaSelecionadaPeloUsuario(conta, usuario.cpfCnpj)
          ) {
            return false;
          }
          if (contaDisponivelParaSelecao(conta, usuario.cpfCnpj)) {
            return true;
          }
          setDisabledCheckAceite(true);
          setAceiteCheck(true);
          return false;
        })
        .map((conta: IAssembleiaContaAssociado): number => {
          return conta.idConvocado;
        }),
    ];
    setIdConvocados(idConvocadosCopy);
  }, [initialized, currentEvent, usuario, idConvocados]);

  const { rankedSize: screenSize } = useScreenSize();

  return (
    <Modal
      preventEscExit={props.preventEscExit || false}
      preventMaskExit={props.preventMaskExit || false}
      modalKey={props.modalKey}
      mobileOn={props.mobileOn}
      title={props.title}
      className={rootClassName}
      onClose={props.onClose}
      size={ModalSizes.ExtraLarge}
      compenseColPaddingContent
      footer={[
        {
          theme: ButtonThemes.Primary,
          leftIcon: <CheckCircleIcon />,
          children: translation.SeletorContasParaVotoModal.confirmLabel,
          'aria-label': translation.SeletorContasParaVotoModal.confirmLabel,
          disabled:
            salvando ||
            idConvocados.length === 0 ||
            (canShowAceite && !aceiteCheck),
          loading: salvando,
          onClick: salvarPresencas,
        },
      ]}
    >
      <Row>
        {canShowAceite ? (
          <Col>
            <p>{translation.SeletorContasParaVotoModal.textAceite}</p>
          </Col>
        ) : undefined}

        {isColaborador && !isExColaborador ? (
          <Col>
            <p className={'nunito'}>
              {translation.SeletorContasParaVotoModal.textPessoaColaborador}
            </p>
          </Col>
        ) : undefined}
        {isExColaborador && isColaborador ? (
          <Col>
            <p className={'nunito'}>
              {translation.SeletorContasParaVotoModal.colaboradorDesligado}
            </p>
          </Col>
        ) : undefined}

        {canShowAceite ? (
          <Col
            style={{
              display: 'flex',
              alignItems: 'center',
              margin: '12px 0px',
            }}
          >
            <ControlLabel
              label={translation.SeletorContasParaVotoModal.confirmAceite}
              control={
                <Checkbox
                  onChange={() => setAceiteCheck((val) => !val)}
                  value={aceiteCheck}
                  theme={CheckboxThemes.Primary}
                  disabled={salvando || disabledCheckAceite}
                />
              }
            />
          </Col>
        ) : undefined}

        {isColaborador && !isExColaborador && (
          <Col>
            <div className={`${rootClassName}-icon-text-container`}>
              <TipoConta />
              <p className={'nunito'}>
                {translation.SeletorContasParaVotoModal.textContaColaborador}
              </p>
            </div>
          </Col>
        )}
        {isExColaborador && isColaborador && (
          <Col>
            <div className={`${rootClassName}-icon-text-container`}>
              <TipoConta />
              <p className={'nunito'}>
                {translation.SeletorContasParaVotoModal.textContaExColaborador}
              </p>
            </div>
          </Col>
        )}

        <Row>
          {screenSize >= ScreenSize.md ? (
            <Row className={`${rootClassName}-header-container`}>
              <Col cols={[2, 2, 2, 2, 2]}>
                <p className={'nunito'}>
                  {translation.SeletorContasParaVotoModal.headerTextConta}
                </p>
              </Col>
              <Col cols={[4, 4, 4, 4, 4]}>
                <p className={'nunito'}>
                  {translation.SeletorContasParaVotoModal.headerTextNomAsso}
                </p>
              </Col>
              <Col cols={[2, 2, 2, 2, 2]}>
                <p className={'nunito'}>
                  {translation.SeletorContasParaVotoModal.headerTextNuc}
                </p>
              </Col>
              <Col cols={[4, 4, 4, 4, 4]}>
                <p className={'nunito'}>
                  {translation.SeletorContasParaVotoModal.headerTextPresen}
                </p>
              </Col>
            </Row>
          ) : undefined}
          {initialized &&
            (currentEvent?.contasDisponiveis || []).map((conta) => {
              return (
                <Row
                  className={`${rootClassName}-body-container`}
                  key={crypto.randomUUID()}
                >
                  {screenSize >= ScreenSize.md ? (
                    <Fragment>
                      <Col
                        cols={[2, 2, 2, 2, 2]}
                        className={`${rootClassName}-conta-container`}
                      >
                        <p>{conta.numeroConta}</p>
                      </Col>
                      <Col cols={[4, 4, 4, 4, 4]}>
                        <div className={`${rootClassName}-name-container`}>
                          {conta.flgColaborador ===
                            FlagColaborador.Colaborador && <TipoConta />}
                          <p>{conta.nomeAssociado}</p>
                        </div>
                      </Col>
                      <Col
                        cols={[2, 2, 2, 2, 2]}
                        className={`${rootClassName}-nucleo-container`}
                      >
                        <p>{conta.numeroNucleo}</p>
                      </Col>
                      <Col cols={[4, 4, 4, 4, 4]}>
                        {!conta.flgTitular ? (
                          <Row>
                            <Col>
                              <p>Irei representar?</p>
                            </Col>
                            <Col className={`${rootClassName}-radio-container`}>
                              <ControlLabel
                                label={'Sim'}
                                control={
                                  <RadioButton
                                    id='radio-button-classic'
                                    theme={RadioButtonType.New}
                                    onChange={() =>
                                      handleSelecionarConta(conta)
                                    }
                                    value={contaSelecionada(conta) === true}
                                    disabled={
                                      salvando ||
                                      contaDisabled(conta) ||
                                      (canShowAceite && !aceiteCheck)
                                    }
                                  />
                                }
                              />
                            </Col>
                            <Col className={`${rootClassName}-radio-container`}>
                              <ControlLabel
                                label={'Não'}
                                control={
                                  <RadioButton
                                    id='radio-button-classic'
                                    theme={RadioButtonType.New}
                                    onChange={() =>
                                      handleSelecionarConta(conta)
                                    }
                                    value={contaSelecionada(conta) === false}
                                    disabled={
                                      salvando ||
                                      contaDisabled(conta) ||
                                      (canShowAceite && !aceiteCheck)
                                    }
                                  />
                                }
                              />
                            </Col>
                          </Row>
                        ) : (
                          <p>Titular</p>
                        )}
                      </Col>
                    </Fragment>
                  ) : (
                    <Fragment>
                      <Row>
                        <Col cols={[8, 8, 8, 8, 8]}>
                          <Row>
                            <Col
                              cols={[6, 6, 6, 6, 6]}
                              className={`${rootClassName}-conta-container`}
                            >
                              <p className={'nunito'}>
                                {
                                  translation.SeletorContasParaVotoModal
                                    .headerTextConta
                                }
                              </p>
                              <p>{conta.numeroConta}</p>
                            </Col>
                            <Col
                              cols={[6, 6, 6, 6, 6]}
                              className={`${rootClassName}-nucleo-container`}
                            >
                              <p className={'nunito'}>
                                {
                                  translation.SeletorContasParaVotoModal
                                    .headerTextNuc
                                }
                              </p>
                              <p>{conta.numeroNucleo}</p>
                            </Col>
                            <Col
                              className={`${rootClassName}-name-mobile-container`}
                            >
                              <p className={'nunito'}>
                                {
                                  translation.SeletorContasParaVotoModal
                                    .headerTextNomAsso
                                }
                              </p>
                              <div
                                className={`${rootClassName}-name-container`}
                              >
                                {conta.flgColaborador ===
                                  FlagColaborador.Colaborador && <TipoConta />}
                                <p>{conta.nomeAssociado}</p>
                              </div>
                            </Col>
                          </Row>
                        </Col>
                        <Col
                          cols={[4, 4, 4, 4, 4]}
                          style={{ borderLeft: '1px solid #efefef' }}
                        >
                          <Row>
                            <Col>
                              <Row
                                style={
                                  conta.flgTitular
                                    ? {
                                        display: 'flex',
                                        flexDirection: 'column',
                                      }
                                    : {}
                                }
                              >
                                <p className={'nunito header-present'}>
                                  {
                                    translation.SeletorContasParaVotoModal
                                      .headerTextPresen
                                  }
                                </p>
                                {!conta.flgTitular ? (
                                  <Fragment>
                                    <Col>
                                      <p
                                        className={'nunito header-present-text'}
                                      >
                                        Irei representar?
                                      </p>
                                    </Col>
                                    <Col
                                      className={`${rootClassName}-radio-container`}
                                    >
                                      <ControlLabel
                                        label={'Sim'}
                                        control={
                                          <RadioButton
                                            id='radio-button-classic'
                                            theme={RadioButtonType.New}
                                            onChange={() =>
                                              handleSelecionarConta(conta)
                                            }
                                            value={
                                              contaSelecionada(conta) === true
                                            }
                                            disabled={
                                              salvando ||
                                              contaDisabled(conta) ||
                                              (canShowAceite && !aceiteCheck)
                                            }
                                          />
                                        }
                                      />
                                    </Col>
                                    <Col
                                      className={`${rootClassName}-radio-container`}
                                    >
                                      <ControlLabel
                                        label={'Não'}
                                        control={
                                          <RadioButton
                                            id='radio-button-classic'
                                            theme={RadioButtonType.New}
                                            onChange={() =>
                                              handleSelecionarConta(conta)
                                            }
                                            value={
                                              contaSelecionada(conta) === false
                                            }
                                            disabled={
                                              salvando ||
                                              contaDisabled(conta) ||
                                              (canShowAceite && !aceiteCheck)
                                            }
                                          />
                                        }
                                      />
                                    </Col>
                                  </Fragment>
                                ) : (
                                  <p>Titular</p>
                                )}
                              </Row>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Fragment>
                  )}
                </Row>
              );
            })}
        </Row>
        {salvando && (
          <Loading text='Aguarde, estamos confirmando sua presença.' inline />
        )}
      </Row>
    </Modal>
  );
}
